import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, X } from "lucide-react";
import { useLeadCapture } from "@/context/LeadCaptureContext";

const PLANS = [
  {
    id: "basic",
    name: "Basic",
    badge: "Starter",
    desc: "För nystartade hunddagis och små verksamheter som vill komma igång snabbt.",
    price: { month: 490, year: 390 },
    features: [
      "Upp till 30 hundar",
      "Bokningssystem & beläggningskarta",
      "Digitala hundprofiler",
      "50 fakturor / månad",
      "Ägar-app (standard branding)",
      "E-postsupport (vardagar)",
    ],
    notIncluded: ["GPS-promenader", "Flera anläggningar", "API & integrationer"],
    cta: "Välj plan",
    testid: "plan-basic",
  },
  {
    id: "pro",
    name: "Professionell",
    badge: "Mest populär",
    desc: "Allt-i-ett för växande pensionat, dagis och team som vill skala professionellt.",
    price: { month: 990, year: 790 },
    features: [
      "Upp till 200 hundar",
      "Alla moduler: Hotell, Dagis, Promenader",
      "Automatisk månadsfakturering (obegränsat)",
      "Live GPS-spår & pass-rapporter",
      "Ägar-app med white-label",
      "Stripe, Klarna & Autogiro",
      "Prioriterad support (chat + telefon)",
      "Onboarding-konsultation 2h",
    ],
    notIncluded: ["Dedikerad CSM"],
    cta: "Välj plan",
    popular: true,
    testid: "plan-pro",
  },
  {
    id: "enterprise",
    name: "Enterprise",
    badge: "Skräddarsytt",
    desc: "För kedjor, franchise och anläggningar med flera orter eller komplexa behov.",
    price: { month: null, year: null },
    features: [
      "Obegränsat antal hundar & personal",
      "Flera anläggningar / orter",
      "Egen dedikerad CSM & onboarding-team",
      "API + integrationer (Fortnox, Visma, m.fl.)",
      "SSO, SAML & avancerad behörighet",
      "SLA: 99.9% uptime garanti",
      "Support 24/7 — telefon, chat, e-post",
      "Anpassade rapporter & datalager",
    ],
    notIncluded: [],
    cta: "Välj plan",
    testid: "plan-enterprise",
  },
];

const FAQ = [
  { q: "Kan jag byta plan när som helst?", a: "Ja, du kan uppgradera eller nedgradera när som helst. Vi prorata-räknar skillnaden." },
  { q: "Finns det bindningstid?", a: "Nej. Månadsabonnemang är uppsägningsbart månadsvis. Årsplaner ger 20% rabatt men kan sägas upp inför nästa period." },
  { q: "Erbjuder ni gratis provperiod?", a: "Ja — 14 dagar gratis på alla planer. Inget kreditkort krävs för att komma igång." },
  { q: "Hjälper ni till med migreringen?", a: "Ja. Vid Professionell och Enterprise ingår onboarding där vi flyttar bokningar, kunder och hundprofiler från ditt nuvarande system." },
];

export default function PricingPage() {
  const [annual, setAnnual] = useState(false);
  const { openLeadCapture } = useLeadCapture();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="page-pricing" className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      <Seo
        title="Priser"
        description="Hundifys prisplaner: Basic, Professionell och Enterprise. Allt-i-ett affärssystem för hundpensionat, hunddagis och hundrastning. 14 dagars gratis prov."
        path="/pricing"
      />
      <Navbar />
      <main className="pt-28">
        {/* Hero */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 h-[420px] w-[700px] blob-emerald opacity-50 pointer-events-none" />
          <div className="mx-auto max-w-3xl px-6 lg:px-12 text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/5 dark:bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold tracking-[0.18em] uppercase text-emerald-700 dark:text-emerald-300">
              Priser
            </div>
            <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 dark:text-white">
              Enkel prissättning, utan överraskningar.
            </h1>
            <p className="mt-5 text-lg text-slate-600 dark:text-slate-300">
              Välj den plan som passar din verksamhet idag. Uppgradera när du växer.
              Alla planer inkluderar 14 dagars gratis prov.
            </p>

            <div className="mt-10 inline-flex items-center rounded-full bg-white dark:bg-slate-800 p-1 border border-slate-200 dark:border-slate-700 text-sm font-medium">
              <button
                data-testid="pricing-toggle-month"
                onClick={() => setAnnual(false)}
                className={`px-4 py-2 rounded-full transition-colors ${
                  !annual ? "bg-slate-900 dark:bg-emerald-500 text-white" : "text-slate-600 dark:text-slate-300"
                }`}
              >
                Månadsvis
              </button>
              <button
                data-testid="pricing-toggle-year"
                onClick={() => setAnnual(true)}
                className={`px-4 py-2 rounded-full transition-colors flex items-center gap-2 ${
                  annual ? "bg-slate-900 dark:bg-emerald-500 text-white" : "text-slate-600 dark:text-slate-300"
                }`}
              >
                Årligen
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${annual ? "bg-emerald-300 text-emerald-900" : "bg-emerald-100 text-emerald-700"}`}>
                  -20%
                </span>
              </button>
            </div>
          </div>
        </section>

        {/* Plans */}
        <section className="pb-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-12 grid md:grid-cols-3 gap-6 items-stretch">
            {PLANS.map((plan) => {
              const price = annual ? plan.price.year : plan.price.month;
              const popular = plan.popular;
              return (
                <div
                  key={plan.id}
                  data-testid={plan.testid}
                  className={`relative flex flex-col rounded-3xl p-8 transition-all ${
                    popular
                      ? "bg-slate-900 text-white border-2 border-emerald-500 shadow-2xl lg:scale-[1.03] lg:-translate-y-1"
                      : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:-translate-y-1 hover:shadow-xl"
                  }`}
                >
                  {popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white px-3 py-1 text-xs font-bold uppercase tracking-wider shadow-lg">
                      <Sparkles className="h-3 w-3" /> Mest populär
                    </div>
                  )}
                  <div>
                    <div className={`text-xs uppercase tracking-[0.18em] font-semibold ${popular ? "text-emerald-300" : "text-emerald-600 dark:text-emerald-400"}`}>
                      {plan.badge}
                    </div>
                    <h3 className={`font-display mt-2 text-2xl font-bold ${popular ? "text-white" : "text-slate-900 dark:text-white"}`}>
                      {plan.name}
                    </h3>
                    <p className={`mt-2 text-sm ${popular ? "text-slate-300" : "text-slate-500 dark:text-slate-400"}`}>
                      {plan.desc}
                    </p>
                  </div>
                  <div className="mt-6 mb-6">
                    {price !== null ? (
                      <div className="flex items-baseline gap-2">
                        <span className={`font-display text-5xl font-bold ${popular ? "text-white" : "text-slate-900 dark:text-white"}`}>
                          {price}
                        </span>
                        <span className={`text-sm ${popular ? "text-slate-300" : "text-slate-500 dark:text-slate-400"}`}>
                          kr / mån
                        </span>
                      </div>
                    ) : (
                      <div className={`font-display text-3xl font-bold ${popular ? "text-white" : "text-slate-900 dark:text-white"}`}>
                        Skräddarsytt
                      </div>
                    )}
                    {price !== null && (
                      <div className={`mt-1 text-xs ${popular ? "text-slate-400" : "text-slate-400"}`}>
                        {annual ? "Faktureras årligen, exkl. moms" : "Exkl. moms"}
                      </div>
                    )}
                  </div>

                  <ul className="space-y-3 flex-1">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-3">
                        <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${popular ? "bg-emerald-500" : "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"}`}>
                          <Check className={`h-3 w-3 ${popular ? "text-white" : ""}`} strokeWidth={3} />
                        </span>
                        <span className={`text-sm ${popular ? "text-slate-200" : "text-slate-700 dark:text-slate-300"}`}>
                          {f}
                        </span>
                      </li>
                    ))}
                    {plan.notIncluded.map((f) => (
                      <li key={f} className="flex items-start gap-3 opacity-50">
                        <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${popular ? "bg-slate-700" : "bg-slate-100 dark:bg-slate-800 text-slate-400"}`}>
                          <X className="h-3 w-3" strokeWidth={3} />
                        </span>
                        <span className={`text-sm line-through ${popular ? "text-slate-400" : "text-slate-500 dark:text-slate-500"}`}>
                          {f}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    data-testid={`${plan.testid}-cta`}
                    onClick={() => openLeadCapture(`Pricing · ${plan.name}`)}
                    className={`mt-8 h-12 rounded-xl font-semibold ${
                      popular
                        ? "bg-emerald-500 hover:bg-emerald-600 text-white shadow-[0_8px_22px_-4px_rgba(16,185,129,0.55)]"
                        : "bg-slate-900 dark:bg-white hover:bg-slate-800 dark:hover:bg-slate-100 text-white dark:text-slate-900"
                    }`}
                  >
                    {plan.cta}
                  </Button>
                </div>
              );
            })}
          </div>

          <div className="mx-auto max-w-3xl px-6 lg:px-12 mt-12 text-center text-sm text-slate-500 dark:text-slate-400">
            Alla planer inkluderar 14 dagars gratis provperiod. Inget kreditkort krävs. Säg upp när som helst.
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 border-t border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/30">
          <div className="mx-auto max-w-4xl px-6 lg:px-12">
            <h2 className="font-display text-3xl font-bold text-slate-900 dark:text-white text-center">
              Vanliga frågor om priser
            </h2>
            <dl className="mt-10 space-y-4">
              {FAQ.map((q) => (
                <div key={q.q} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
                  <dt className="font-display text-lg font-semibold text-slate-900 dark:text-white flex items-start gap-2">
                    <Check className="mt-1 h-4 w-4 text-emerald-500 shrink-0" />
                    {q.q}
                  </dt>
                  <dd className="mt-2 ml-6 text-slate-600 dark:text-slate-300 leading-relaxed">{q.a}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
